/**
 * Client-side GST preview — mirrors erp-api's `utils/gst.js` (`calculateGST`,
 * `determineGSTType`) exactly, so the live number shown while building an
 * invoice matches what the server will actually post. This is a PREVIEW only:
 * the server recomputes and persists the authoritative figures on save.
 */

export type GstType = 'CGST_SGST' | 'IGST' | 'EXEMPT';

export interface GstBreakup {
  taxableAmount: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  totalGstAmount: number;
  totalAmount: number;
  gstType: GstType;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Same-state -> CGST+SGST, different-state -> IGST, no state on either side -> EXEMPT. */
export const determineGstType = (sellerState?: string | null, buyerState?: string | null): GstType => {
  if (!sellerState || !buyerState) return 'EXEMPT';
  return sellerState.trim().toLowerCase() === buyerState.trim().toLowerCase() ? 'CGST_SGST' : 'IGST';
};

export const calculateGst = (
  taxableAmount: number,
  gstRate: number,
  sellerState?: string | null,
  buyerState?: string | null,
): GstBreakup => {
  const gstType = gstRate === 0 ? 'EXEMPT' : determineGstType(sellerState, buyerState);

  let cgstRate = 0;
  let sgstRate = 0;
  let igstRate = 0;
  if (gstType === 'CGST_SGST') {
    cgstRate = gstRate / 2;
    sgstRate = gstRate / 2;
  } else if (gstType === 'IGST') {
    igstRate = gstRate;
  }

  const cgstAmount = round2((taxableAmount * cgstRate) / 100);
  const sgstAmount = round2((taxableAmount * sgstRate) / 100);
  const igstAmount = round2((taxableAmount * igstRate) / 100);
  const totalGstAmount = round2(cgstAmount + sgstAmount + igstAmount);

  return {
    taxableAmount: round2(taxableAmount),
    cgstRate,
    sgstRate,
    igstRate,
    cgstAmount,
    sgstAmount,
    igstAmount,
    totalGstAmount,
    totalAmount: round2(taxableAmount + totalGstAmount),
    gstType,
  };
};

/** Line = qty*rate, minus discount%, then GST on the taxable balance — same order as the server. */
export const calculateLineGst = (
  quantity: number,
  unitPrice: number,
  discountPercent: number,
  gstRate: number,
  sellerState?: string | null,
  buyerState?: string | null,
): GstBreakup => {
  const lineAmount = quantity * unitPrice;
  const discountAmount = (lineAmount * discountPercent) / 100;
  const taxableAmount = lineAmount - discountAmount;
  return calculateGst(taxableAmount, gstRate, sellerState, buyerState);
};
